import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { boolean, fallback, nullish, number, object, string } from "valibot";
import { dataName, label, rememberInput, required } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { DECORATIVE_IMAGE_ALT, TEXT_CONDITION_KEYS } from "../../constants";
import { useFormServices } from "../../services/formServices";
import FieldValueReference from "../FieldValueReference";
import { defaultStringFilter } from "../../pages/customer/form/filter";
import { getDynamicValueIcon } from "../../utils/menu/optionIconUtil";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { FieldButtonPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import { Show } from "solid-js";
import { LocalImage } from "../../components/image/Image";
import { keyFromSelector, t } from "i18next";
import fieldMapPreview from "../../assets/editor/field-map-preview.webp";

const Schema = object({
  label_text: label(),
  data_name: dataName(),
  initial_current_location: fallback(boolean(), true),
  required: required(),
  remember_input: rememberInput(),
});
const ValueSchema = object({
  coordinates: object({
    latitude: number(),
    longitude: number(),
  }),
  location: nullish(
    object({
      descriptiveName: nullish(string()),
      road: nullish(string()),
      houseNumber: nullish(string()),
      city: nullish(string()),
      postcode: nullish(string()),
      country: nullish(string()),
    }),
  ),
  formattedValue: string(),
});

export type LocationFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const LocationField: Field<LocationFieldProperties, DefaultDtoType, ValueType> = {
  type: "location",
  category: "location",
  widget: "com.moreapps:location:1",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    initial_current_location: true,
    required: false,
    remember_input: false,
  }),
  ruleBehaviour: {
    conditions: { keys: TEXT_CONDITION_KEYS, variant: "text" },
    actions: { variant: "text" },
  },
  filter: {
    type: "string",
    definition: defaultStringFilter,
  },
  stringValue: (value) => value.formattedValue,
  renderValue: (value) => `${value.formattedValue}, ${value.coordinates.latitude}, ${value.coordinates.longitude}`,
  icon: "locationMarker",
  titleKey: keyFromSelector(($) => $.LOCATION_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.LOCATION_FIELD_DESCRIPTION),
  propertyKeys: {
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    initial_current_location: keyFromSelector(($) => $.FIELD_PROPERTY_INITIAL_CURRENT_LOCATION),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  valueReferenceOptions: (id, props, options) => {
    const includeMapAndAddress = options?.[LocationField.type]?.includeMapAndAddress;

    return [
      {
        key: [],
        label: keyFromSelector(($) => $.LOCATION_FIELD_SUB_VALUE_MAP_AND_ADDRESS_FORMATTED_VALUE),
        isSelectable: includeMapAndAddress === true,
      },
      { key: ["formattedValue"], label: keyFromSelector(($) => $.LOCATION_FIELD_SUB_VALUE_FORMATTED_VALUE) },
      {
        key: ["coordinates", "latitude"],
        label: keyFromSelector(($) => $.LOCATION_FIELD_SUB_VALUE_COORDINATES_LATITUDE),
      },
      {
        key: ["coordinates", "longitude"],
        label: keyFromSelector(($) => $.LOCATION_FIELD_SUB_VALUE_COORDINATES_LONGITUDE),
      },
    ].map(({ key, label, isSelectable }) => {
      const referenceId = new FieldValueReference(id, key);
      const icon = getDynamicValueIcon(referenceId, LocationField.type);
      return {
        variant: "dynamicValue",
        value: referenceId,
        label: t(label),
        pill: props.data_name,
        icon,
        isSelectable,
      };
    });
  },
  PreviewContent: (props) => {
    const { t } = useTranslation();

    return (
      <Show
        when={props.properties.initial_current_location}
        fallback={
          <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
            <FieldButtonPreview text={t(($) => $.LOCATION_FIELD_PREVIEW_ADD_BUTTON_LABEL)} icon="map" />
          </PreviewContentLabel>
        }
      >
        <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
          <LocalImage size="lg" cover src={fieldMapPreview} alt={DECORATIVE_IMAGE_ALT} />
        </PreviewContentLabel>
      </Show>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
          <Toggle
            block
            label={t(props.propertyKeys.initial_current_location)}
            checked={props.data.properties.initial_current_location}
            setChecked={(value) => props.setValue("initial_current_location", value)}
            description={t(($) => $.FIELD_PROPERTY_INITIAL_CURRENT_LOCATION_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(LocationField);
